/**
 * سربرگِ «پرامپت‌ها» — کرالر کجا از مدل می‌پرسد، و چه می‌پرسد (WHOSUP-215).
 *
 * ═══ چرا وجود دارد ═══
 *
 * مدل چند جای کرالر می‌دود — کشفِ منبع، غربالگریِ پیش‌نویس، ژئوکدینگ — و
 * هیچ‌کدامش از این سمت دیده نمی‌شد: متنِ پرامپت در مخزنِ event_crawler است،
 * خرجش هیچ‌جا جمع نمی‌شد، و جوابِ مدل روی ترمینال چاپ می‌شد و می‌مرد. این
 * صفحه هر چهار سؤال را یک‌جا جواب می‌دهد: چه پرامپتی، چرا و چه وقت، چقدر، و
 * آخرین بار چه رفت و چه برگشت.
 *
 * ═══ ⚠ دو نیمه‌ی این صفحه یکی نیستند ═══
 *
 *   بالا  **رجیستری** — از `crawl sync-prompts` می‌آید. متنِ پرامپت و
 *         «چرا/چه وقت/چگونه» فقط از این‌جا در می‌آید.
 *   پایین **ردِ پای مدل** — از جدول‌هایی که از قبل هستند
 *         (`discovery_runs`، `inspections`، `building_geocodes`). می‌گوید
 *         مدل کجا و چقدر دویده، ولی پرامپتش را نمی‌داند.
 *
 * قاطی نشدنشان عمدی است: ردیفی که ستونِ «متن»ش برای همیشه خالی است، اگر
 * کنارِ ردیف‌های رجیستری بنشیند، معلوم نمی‌کند هنوز نیامده یا اصلاً از این
 * راه نمی‌آید. نوارِ بالای صفحه (`readiness`) هم برای همین است: صفحه‌ی
 * خالی سه دلیلِ متفاوت دارد و هر سه یک شکل‌اند مگر کسی بگوید کدام است.
 *
 * ═══ چرا حکم این‌جا نیست ═══
 *
 * «خراب/مشکوک/خاموش» را `core/lib/promptReport.ts` تصمیم می‌گیرد و
 * `npm test` می‌آزمایدش. همان تقسیمِ کاری که `sourceTest` و «آدرس‌ها»
 * دارند — آستانه‌ها تصمیمِ نمایشی‌اند و عوض کردنشان نباید مایگریشن بخواهد.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import { formatDateTime } from '@/core/lib/dates';
import { describeError } from '@/core/lib/report';
import {
  buildPromptReport, fmtMs, fmtRate, fmtTokens, fmtUsd,
  READINESS_NOTE, TRACE_META, VERDICT_ICON, VERDICT_LABEL,
  type ModelTrace, type PromptAssessed, type PromptCall,
  type PromptTally, type TraceKey,
} from '@/core/lib/promptReport';
import { Hint } from '@/ui/Hint';
import { Modal } from '@/ui/Modal';
import { fmtNum as fmt } from '@/ui/Num';

/** «claude-opus-5 ×۴۰ · claude-haiku-4-5 ×۲» — دو ستون نمی‌خواهد، یک خط بس است. */
function Tallies({ list }: { list: PromptTally[] | null | undefined }) {
  if (!list || !list.length) return <span className="dim">—</span>;
  return (
    <>
      {list.map((t, i) => (
        <span key={t.key}>
          {i > 0 ? <span className="dim"> · </span> : null}
          <span className="mono ltr">{t.key}</span>
          <span className="dim"> ×{fmt(t.n)}</span>
        </span>
      ))}
    </>
  );
}

/** عددِ درشتِ یک کارتِ آماری. */
function Card({ n, label, help, tone }: {
  n: React.ReactNode; label: string; help?: string; tone?: 'warn' | 'good';
}) {
  return (
    <div className={`card stat${tone ? ` ${tone}` : ''}`}>
      <div className="n">{n}</div>
      <div className="k"><Hint text={label} help={help} /></div>
    </div>
  );
}

/**
 * یک تماس، باز شده: چه رفت، چه برگشت، چه در آمد.
 *
 * ⚠ سه حالتِ «متن نیست» با هم فرق دارند و هر سه گفته می‌شوند: بریده شده،
 * تهی شده (بیست تماسِ تازه‌تر آمده)، یا اصلاً فرستاده نشده. یک «—» برای
 * هر سه یعنی آدم دنبالِ باگی می‌گردد که نیست.
 */
function CallBody({ c }: { c: PromptCall }) {
  const empty = (what: string) => (
    <p className="dim">
      {c.body_dropped
        ? `${what} نگه داشته نشده: بیست تماسِ تازه‌تر از این آمده و متنش تهی شده.`
        : `${what} ثبت نشده — کرالر برای این تماس متنی نفرستاده.`}
    </p>
  );
  return (
    <>
      <h4>آنچه فرستاده شد</h4>
      {c.request ? <pre className="raw-json">{c.request}</pre> : empty('ورودی')}
      <h4>آنچه برگشت</h4>
      {c.ok ? null : (
        <p className="err">{c.error ?? 'تماس شکست خورد و دلیلش ثبت نشده.'}</p>
      )}
      {c.response ? <pre className="raw-json">{c.response}</pre> : empty('جواب')}
      {c.truncated ? (
        <p className="dim">⚠ متن بریده شده — کرالر فقط ابتدایش را فرستاده.</p>
      ) : null}
      {Object.keys(c.outcome ?? {}).length ? (
        <>
          <h4><Hint text="چه چیزی از جواب در آمد"
            help="خلاصه‌ای که خودِ کرالر نوشته: چند کاندیدا، چه حکمی، چه مختصاتی." /></h4>
          <pre className="raw-json">{JSON.stringify(c.outcome, null, 2)}</pre>
        </>
      ) : null}
    </>
  );
}

/** پنجره‌ی یک پرامپت: متنش، و آخرین تماس‌هایش. */
function PromptModal({ row, onClose }: { row: PromptAssessed; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['promptDetail', row.key],
    queryFn: () => opsRepo.promptDetail(row.key),
  });
  // تماسی که باز است. پیش‌فرض تازه‌ترین — چون سؤالِ همیشگی «آخرین بار چه شد».
  const [open, setOpen] = useState<number | null>(null);

  const d = q.data;
  const calls = d?.calls ?? [];
  const current = calls.find((c) => c.id === open) ?? calls[0] ?? null;

  return (
    <Modal wide title={row.title ?? row.key}
      hint={row.registered ? undefined
        : 'این کلید در رجیستری نیست — فقط از لاگِ تماس‌ها شناخته می‌شود.'}
      onClose={onClose}>
      {q.isLoading ? <p className="dim">…</p> : null}
      {q.error ? <p className="err">{describeError(q.error)}</p> : null}

      {/* ⚠ آدرس بالای همه‌چیز می‌نشیند و نه در `hint`ِ ریزِ زیرِ عنوان: آن
          جا بود و دیده نمی‌شد. سوالِ «این متن از کجا می‌آید» همان‌قدر مهم
          است که خودِ متن. */}
      <Where sourcePath={d?.prompt?.source_path ?? row.source_path ?? null} />

      {d?.prompt ? (
        <>
          {d.prompt.purpose ? (<><h4>چرا</h4><p>{d.prompt.purpose}</p></>) : null}
          {d.prompt.when_used ? (<><h4>چه وقت</h4><p>{d.prompt.when_used}</p></>) : null}
          {d.prompt.how ? (<><h4>چگونه</h4><p>{d.prompt.how}</p></>) : null}
          {d.prompt.cost_note ? (<><h4>خرج</h4><p>{d.prompt.cost_note}</p></>) : null}
          <h4>
            <Hint text="متنِ پرامپت"
              help="آینه‌ی چیزی که در مخزنِ event_crawler است. این‌جا ویرایش‌شدنی نیست و نباید باشد — یک نویسنده، یک متن." />
            {d.prompt.version ? <span className="dim"> · نسخه {d.prompt.version}</span> : null}
          </h4>
          {d.prompt.template
            ? <pre className="raw-json">{d.prompt.template}</pre>
            : <p className="dim">رجیستری متنِ پرامپت را نفرستاده.</p>}
        </>
      ) : null}

      {row.notes.length ? (
        <>
          <h4>یافته‌ها</h4>
          <ul>
            {row.notes.map((n, i) => (
              <li key={i} className={n.tone === 'bad' ? 'err' : (n.tone === 'info' ? 'dim' : '')}>
                {n.text}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h4>آخرین تماس‌ها</h4>
      {!q.isLoading && !calls.length ? (
        <p className="dim">هیچ تماسی برای این کلید ثبت نشده.</p>
      ) : null}
      {calls.length ? (
        <div className="chiprow">
          {calls.map((c) => (
            <button key={c.id} type="button"
              className={`chip${current?.id === c.id ? ' on' : ''}${c.ok ? '' : ' warn'}`}
              title={`${c.model ?? '—'} · ${fmtMs(c.duration_ms)} · ${fmtUsd(c.cost_usd)}`}
              onClick={() => setOpen(c.id)}>
              {c.ok ? '' : '✕ '}
              <span className="ltr">{formatDateTime(c.called_at)}</span>
            </button>
          ))}
        </div>
      ) : null}

      {current ? (
        <>
          <p className="note">
            <span className="mono ltr">{current.model ?? '—'}</span>
            {current.prompt_version ? <> · نسخه <span className="mono ltr">{current.prompt_version}</span></> : null}
            {current.target ? <> · روی <span className="mono ltr">{current.target}</span></> : null}
            {current.city ? <> · {current.city}</> : null}
            {' · '}{fmtMs(current.duration_ms)}
            {' · '}{fmtUsd(current.cost_usd)}
            {' · '}{fmtTokens(current.input_tokens)}↑ {fmtTokens(current.output_tokens)}↓
          </p>
          <CallBody c={current} />
        </>
      ) : null}

      <button className="btn ghost" type="button" style={{ marginTop: 12 }}
        onClick={onClose}>بستن</button>
    </Modal>
  );
}

/**
 * آدرسِ یک پرامپت — و «آدرس» این‌جا دو چیزِ متفاوت است که هر دو لازم‌اند.
 *
 * ⚠ فایلِ پایتون در مخزنِ دیگری است و مسیرش فقط برای کشف مستند شده؛ ولی
 * **آدرسِ خودِ متن در دیتابیس** همیشه معلوم است و برای کسی که می‌خواهد
 * خودش کوئری بزند، همان به‌دردبخورتر است. پس هر دو نشان داده می‌شوند و
 * نبودنِ اولی صریح گفته می‌شود، نه با یک «—»ی مبهم.
 */
function Where({ sourcePath, origin }: {
  sourcePath: string | null; origin?: string | null;
}) {
  return (
    <p className="note">
      <b>آدرسِ فایل:</b>{' '}
      {sourcePath
        ? <span className="mono ltr">{sourcePath}</span>
        : <span className="dim">
            در مخزنِ <span className="mono ltr">event_crawler</span> است ولی مسیرش
            از این‌جا معلوم نیست — با <span className="mono ltr">crawl sync-prompts</span> می‌آید.
          </span>}
      {origin ? (
        <> · <b>متن در دیتابیس:</b> <span className="mono ltr">{origin}</span></>
      ) : null}
    </p>
  );
}

/**
 * متنِ واقعیِ یک ردِ پا، و چند تماسِ واقعی‌اش.
 *
 * ⚠ دو متن این‌جا هست و یکی نیستند: «قالب» چیزی است که نوشته شده، و
 * «فرستاده‌شده» همان قالبِ پُرشده‌ی یک تماسِ مشخص. کسی که می‌پرسد «چرا مدل
 * این جواب را داد» دومی را می‌خواهد.
 */
function TraceModal({ k, onClose }: { k: TraceKey; onClose: () => void }) {
  const meta = TRACE_META[k];
  const q = useQuery({
    queryKey: ['promptTrace', k],
    queryFn: () => opsRepo.promptTrace(k),
  });
  const [open, setOpen] = useState<number>(0);

  const d = q.data;
  const samples = d?.samples ?? [];
  const current = samples[open] ?? samples[0] ?? null;

  return (
    <Modal wide title={meta.title} hint={meta.table} onClose={onClose}>
      {q.isLoading ? <p className="dim">…</p> : null}
      {q.error ? <p className="err">{describeError(q.error)}</p> : null}

      <Where sourcePath={meta.sourcePath} origin={d?.prompt?.origin ?? null} />

      <h4>
        قالبِ پرامپت
        {d?.prompt?.version ? <span className="dim"> · نسخه {d.prompt.version}</span> : null}
      </h4>
      {d?.prompt?.text ? (
        <pre className="raw-json">{d.prompt.text}</pre>
      ) : (
        <p className="dim">
          {meta.noPromptWhy ?? 'متنِ قالب در دیتابیس ذخیره نشده.'}
        </p>
      )}

      {d?.versions?.length ? (
        <>
          <h4><Hint text="نسخه‌های ثبت‌شده"
            help="بیش از یکی یعنی قالب عوض شده — و تاریخش می‌گوید کِی." /></h4>
          <div className="scrollx">
            <table className="tbl">
              <thead><tr><th>نسخه</th><th>نویسه</th><th>ردیف</th><th>از کِی</th></tr></thead>
              <tbody>
                {d.versions.map((v) => (
                  <tr key={v.version}>
                    <td className="mono ltr">{v.version}</td>
                    <td className="num">{v.chars != null ? fmt(v.chars) : '—'}</td>
                    <td className="num">{v.n != null ? fmt(v.n) : '—'}</td>
                    <td className="num">{formatDateTime(v.added_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      <h4>تماس‌های واقعی</h4>
      {!q.isLoading && !samples.length ? (
        <p className="dim">هنوز تماسی ثبت نشده.</p>
      ) : null}
      {samples.length ? (
        <div className="chiprow">
          {samples.map((s, i) => (
            <button key={`${s.at}-${i}`} type="button"
              className={`chip${(current === s) ? ' on' : ''}`}
              title={s.target ?? undefined}
              onClick={() => setOpen(i)}>
              <span className="ltr">{formatDateTime(s.at)}</span>
              {s.target ? <span className="dim"> · {s.target}</span> : null}
            </button>
          ))}
        </div>
      ) : null}

      {current ? (
        <>
          <p className="note">
            {current.model ? <><span className="mono ltr">{current.model}</span>{' · '}</> : null}
            {current.version ? <>نسخه <span className="mono ltr">{current.version}</span>{' · '}</> : null}
            {current.target ? <span className="mono ltr">{current.target}</span> : null}
            {current.confidence ? <> · {current.confidence}</> : null}
          </p>
          <h4>آنچه فرستاده شد</h4>
          {current.asked ? (
            <pre className="raw-json">{current.asked}</pre>
          ) : (
            <p className="dim">
              متنِ این تماس ذخیره نشده — {meta.noPromptWhy ?? 'این جدول نگهش نمی‌دارد.'}
            </p>
          )}
          <h4>آنچه برگشت</h4>
          <pre className="raw-json">{JSON.stringify(current.answered, null, 2)}</pre>
          {current.reasons?.length ? (
            <>
              <h4>دلیل‌ها</h4>
              <pre className="raw-json">{current.reasons.join('\n')}</pre>
            </>
          ) : null}
        </>
      ) : null}

      <button className="btn ghost" type="button" style={{ marginTop: 12 }}
        onClick={onClose}>بستن</button>
    </Modal>
  );
}

/** یک ردِ پا: آنچه از روی جدولِ خودش می‌شود گفت — حالا با متنِ خودِ پرامپت. */
function TraceCard({ k, t }: { k: TraceKey; t: ModelTrace }) {
  const meta = TRACE_META[k];
  const extra = t[meta.extraKey];
  const [open, setOpen] = useState(false);
  return (
    <div className="card">
      <h2>
        {meta.title}
        <span className="dim"> · <span className="mono ltr">{meta.table}</span></span>
      </h2>
      <p className="note">{meta.what}</p>
      <p className="note"><b>چه وقت:</b> {meta.when}</p>
      <Where sourcePath={meta.sourcePath} origin={t.prompt_origin} />
      {/* دکمه بالای اعداد می‌نشیند و نه ته کارت: سوالِ اولِ این صفحه «چه
          می‌پرسیم» است و آمار جوابِ دوم. */}
      {t.has_prompt ? (
        <button className="btn" type="button" onClick={() => setOpen(true)}>
          دیدنِ متنِ پرامپت
          <span className="dim"> · {fmt(t.prompt_chars ?? 0)} نویسه</span>
        </button>
      ) : (
        <p className="note err">
          متنِ این پرامپت هیچ‌جای دیتابیس نیست.{meta.noPromptWhy ? ` ${meta.noPromptWhy}` : ''}
          {' '}
          <button type="button" className="linkbtn" onClick={() => setOpen(true)}>
            جواب‌های واقعی‌اش را ببین
          </button>
        </p>
      )}
      {open ? <TraceModal k={k} onClose={() => setOpen(false)} /> : null}
      <div className="grid n4" style={{ marginTop: 10 }}>
        <Card n={fmt(t.runs)} label={meta.unitLabel} help="روی هم، از ابتدای ثبت." />
        <Card n={fmt(t.runs_d10)} label="۱۰ روزِ اخیر" />
        <Card n={typeof extra === 'number' ? fmt(extra) : '—'} label={meta.extraLabel} />
        <Card n={<span className="ltr">{formatDateTime(t.last_at) || '—'}</span>}
          label="آخرین بار" />
      </div>
      <div className="scrollx">
        <table className="tbl">
          <tbody>
            <tr><th>مدل</th><td><Tallies list={t.by_model} /></td></tr>
            <tr>
              <th><Hint text="نسخه‌ی قالب"
                help="شماره‌ای که خودِ کرالر روی ردیف نوشته. بیش از یکی یعنی قالب در این بازه عوض شده." /></th>
              <td><Tallies list={t.by_version} /></td>
            </tr>
          </tbody>
        </table>
      </div>
      {t.recent?.length ? (
        <details style={{ marginTop: 8 }}>
          <summary className="dim">ده ردیفِ آخر</summary>
          <pre className="raw-json">{JSON.stringify(t.recent, null, 2)}</pre>
        </details>
      ) : null}
    </div>
  );
}

export function PromptsTab() {
  const q = useQuery({ queryKey: ['prompts'], queryFn: () => opsRepo.prompts() });
  const [open, setOpen] = useState<string | null>(null);

  if (q.isLoading) return <p className="dim">…</p>;
  // ⚠ نه String(q.error): خطای PostgREST یک شیءِ ساده است و String ازش
  // «[object Object]» می‌سازد — همان چیزی که وقتی مایگریشن روی دیتابیس
  // نباشد دیده می‌شود.
  if (q.error) return <div className="card"><p className="err">{describeError(q.error)}</p></div>;

  const r = buildPromptReport(q.data!);
  const t = r.totals;
  const current = r.rows.find((x) => x.key === open) ?? null;

  return (
    <>
      {/* نوارِ «این صفحه در چه حالی است» — قبل از هر عددی، چون بی آن هر
          عددِ صفر معلوم نیست خبر است یا نبودِ خبر. */}
      <p className="note legend">
        {READINESS_NOTE[r.readiness]}
        {r.registry.last_sync ? (
          <> آخرین همگام‌سازیِ رجیستری:{' '}
            <span className="ltr">{formatDateTime(r.registry.last_sync)}</span>.</>
        ) : null}
      </p>

      <div className="grid n5" style={{ marginTop: 12 }}>
        <Card n={fmt(r.registry.count)} label="پرامپتِ ثبت‌شده"
          help={`${fmt(r.registry.enabled)} تاشان روشن‌اند. این عدد از رجیستری می‌آید — یعنی آنچه کرالر اعلام کرده، نه آنچه واقعاً زده.`} />
        <Card n={fmt(t.calls_d10)} label={`تماس در ${r.days} روز`}
          help={`روی هم ${fmt(t.calls)} تماس از ابتدای ثبت.`} />
        <Card n={fmt(t.failed_d10)} label="تماسِ خطادار"
          tone={t.failed_d10 > 0 ? 'warn' : undefined}
          help={`در ${r.days} روزِ اخیر.`} />
        <Card n={fmtUsd(t.cost_d10)} label={`خرج در ${r.days} روز`}
          help={`روی هم ${fmtUsd(t.cost)} از ابتدای ثبت. صفر بودنش یعنی کرالر هزینه را نمی‌فرستد، نه اینکه رایگان بوده.`} />
        <Card n={<>{fmtTokens(t.tokens_in)}<span className="dim"> / </span>{fmtTokens(t.tokens_out)}</>}
          label="توکنِ ورودی / خروجی" help="روی هم، از ابتدای ثبت." />
      </div>

      {r.unregistered.length ? (
        <div className="card">
          <p className="err">
            {r.unregistered.length} کلید در لاگِ تماس‌ها هست که در رجیستری نیست
            ({r.unregistered.map((x) => x.key).join('، ')}). یعنی کرالر پرامپتی
            می‌زند که ثبتش نکرده — <span className="mono ltr">crawl sync-prompts</span> عقب است.
          </p>
        </div>
      ) : null}

      <div className="card">
        <h2>
          <Hint text="پرامپت‌ها"
            help="هر ردیف یک جایی است که کرالر از مدل می‌پرسد. روی ردیف بزن تا متنِ پرامپت و آخرین تماس‌هایش را ببینی." />
        </h2>
        {!r.rows.length ? (
          <p className="dim" style={{ marginTop: 10 }}>
            هنوز نه رجیستری‌ای رسیده نه تماسی ثبت شده. تا آن موقع، «ردِ پای مدل»
            پایینِ همین صفحه تنها چیزی است که می‌شود گفت.
          </p>
        ) : (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>حکم</th>
                  <th>کلید</th>
                  <th><Hint text="فایل" help="کجای مخزنِ event_crawler. رجیستری می‌فرستدش؛ خالی یعنی هنوز نفرستاده." /></th>
                  <th><Hint text="متن" help="طولِ قالبِ پرامپت. روی ردیف بزن تا خودش را ببینی." /></th>
                  <th>فاز</th>
                  <th><Hint text={`تماس (${r.days} روز)`} help="سمتِ راست در پنجره، سمتِ چپ روی هم." /></th>
                  <th><Hint text="خطا" help="نسبتِ خطا در پنجره. زیرِ سه تماس عددی نشان داده نمی‌شود — «۱۰۰٪» از دو تماس حکم نیست." /></th>
                  <th><Hint text="p50 / p95" help="زمانِ پاسخِ مدل." /></th>
                  <th><Hint text="خرج" help="سمتِ راست در پنجره، سمتِ چپ روی هم." /></th>
                  <th>توکن ↑/↓</th>
                  <th>مدل</th>
                  <th><Hint text="نسخه" help="آنچه واقعاً رفته. اگر با نسخه‌ی رجیستری فرق کند، در یافته‌ها گفته می‌شود." /></th>
                  <th>آخرین تماس</th>
                </tr>
              </thead>
              <tbody>
                {r.rows.map((x) => (
                  <tr key={x.key} style={{ cursor: 'pointer' }}
                    onClick={() => setOpen(x.key)}>
                    <td>
                      <span title={x.notes.map((n) => n.text).join('\n') || VERDICT_LABEL[x.verdict]}>
                        {VERDICT_ICON[x.verdict]} {VERDICT_LABEL[x.verdict]}
                      </span>
                    </td>
                    <td>
                      <span className="linkbtn mono ltr">{x.key}</span>
                      {x.title ? <div className="dim">{x.title}</div> : null}
                    </td>
                    <td className="mono ltr" title={x.source_path ?? undefined}>
                      {x.source_path ?? <span className="dim">—</span>}
                    </td>
                    <td className="num">
                      {x.template_chars
                        ? `${fmt(x.template_chars)} نویسه`
                        : <span className="dim">—</span>}
                    </td>
                    <td className="mono ltr">{x.stage ?? '—'}</td>
                    <td className="num">
                      {fmt(x.calls_d10)}<span className="dim"> / {fmt(x.calls)}</span>
                    </td>
                    <td className={`num${(x.failRate ?? 0) > 0 ? ' bad' : ''}`}>
                      {fmtRate(x.failRate)}
                    </td>
                    <td className="num">
                      {fmtMs(x.p50_ms)}<span className="dim"> / {fmtMs(x.p95_ms)}</span>
                    </td>
                    <td className="num">
                      {fmtUsd(x.cost_usd_d10)}<span className="dim"> / {fmtUsd(x.cost_usd)}</span>
                    </td>
                    <td className="num">
                      {fmtTokens(x.tokens_in)}<span className="dim"> / {fmtTokens(x.tokens_out)}</span>
                    </td>
                    <td><Tallies list={x.models} /></td>
                    <td className="mono ltr">{x.usedVersion ?? x.declared_version ?? '—'}</td>
                    <td className="num">{formatDateTime(x.last_at) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {q.data!.recent.length ? (
        <div className="card">
          <h2>
            <Hint text="تازه‌ترین تماس‌ها"
              help="چهل تماسِ آخر، از همه‌ی پرامپت‌ها با هم — برای وقتی که سؤال «الان چه خبر است» باشد و نه «این پرامپت چطور است»." />
          </h2>
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>کِی</th><th>پرامپت</th><th>مدل</th><th>شهر</th><th>روی چه</th>
                  <th>زمان</th><th>خرج</th><th>توکن ↑/↓</th><th>نتیجه</th>
                </tr>
              </thead>
              <tbody>
                {q.data!.recent.map((c) => (
                  <tr key={c.id}>
                    <td className="num">{formatDateTime(c.called_at)}</td>
                    <td>
                      <button type="button" className="linkbtn mono ltr"
                        onClick={() => setOpen(c.prompt_key)}>{c.prompt_key}</button>
                    </td>
                    <td className="mono ltr">{c.model ?? '—'}</td>
                    <td>{c.city ?? <span className="dim">—</span>}</td>
                    <td className="mono ltr" title={c.target ?? undefined}>
                      {c.target ?? '—'}
                    </td>
                    <td className="num">{fmtMs(c.duration_ms)}</td>
                    <td className="num">{fmtUsd(c.cost_usd)}</td>
                    <td className="num">
                      {fmtTokens(c.input_tokens)}<span className="dim"> / </span>
                      {fmtTokens(c.output_tokens)}
                    </td>
                    <td>
                      {c.ok
                        ? <span className="chip">درست</span>
                        : <span className="err" title={c.error ?? undefined}>✕ خطا</span>}
                      {c.has_body ? null : <span className="dim"> · بی‌متن</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* ═══ نیمه‌ی دوم: آنچه بی‌رجیستری هم می‌دانیم ═══ */}
      <div className="card">
        <h2>
          <Hint text="ردِ پای مدل در جدول‌های کرالر"
            help="این‌ها رجیستری نیستند، ولی متنِ پرامپتشان را دارند: کرالر از قبل ذخیره‌اش می‌کند و این بلوک همان را می‌خواند." />
        </h2>
        <p className="note">
          این بلوک به رجیستری و به ثبتِ تماس <b>بند نیست</b> و از روزِ اول کار
          می‌کند: متنِ پرامپت را از همان‌جایی می‌خواند که کرالر از قبل
          می‌نویسدش (<span className="mono ltr">crawler.discovery_prompts.body</span> و{' '}
          <span className="mono ltr">crawler.building_geocodes.asked</span>) — مایگریشنِ
          ۰۱۴۳. آدرسِ فایلِ پایتون ولی جای دیگری است و فقط با{' '}
          <span className="mono ltr">crawl sync-prompts</span> می‌آید؛ هرجا آن را
          نداریم، همین‌جا صریح گفته می‌شود و حدس زده نمی‌شود.
        </p>
      </div>
      {r.traces.length
        ? r.traces.map((x) => <TraceCard key={x.key} k={x.key} t={x.trace} />)
        : (
          <div className="card">
            <p className="dim">
              اسکیمای <span className="mono ltr">crawler</span> روی این دیتابیس
              نیست (یا هنوز خالی است) — پس ردِ پایی هم نیست. روی دیتابیسِ لوکال
              این حالت عادی است.
            </p>
          </div>
        )}
    </>
  );
}
